import { isMobile } from "react-device-detect";
import toast from "react-hot-toast";
import { TOAST_MESSAGES } from "@/lang/toast-messages.lang";
import { isNil } from "lodash";
import { logger } from "@/services/LoggerService";

interface WebShareData {
    text: string;
}

interface MobileShareData {
    title?: string;
    text: string;
    url: string;
}

interface ShareData {
    web: WebShareData;
    mobile: MobileShareData;
    skipToast?: boolean;
}

interface SocialShareData {
    web?: { text: string };
    mobile?: { text: string; url: string };
    skipToast?: boolean;
}

/**
 * Triggers the share functionality.
 *
 * This function copies the share text to the clipboard for desktop users and uses the native mobile
 * share functionality for mobile users.
 *
 * @param shareData - The share data containing web and mobile properties.
 * @throws Throws an error if there is an issue with the share functionality.
 *
 * @example
 * // Example usage:
 * const shareData = {
 *   web: {
 *     text: "Check out this amazing content!",
 *   },
 *   mobile: {
 *     title: "Share Content",
 *     text: "Check out this amazing content!",
 *     url: "https://example.com",
 *   },
 * };
 * await triggerShare(shareData);
 */
export const triggerShare = async (shareData: ShareData): Promise<void> => {
    const { web, mobile, skipToast } = shareData;

    // Copy the share text to clipboard for desktop users
    if (!isMobile) {
        // In safari desktop, we need add setTimeout before navigator.clipboard
        // https://stackoverflow.com/questions/62327358/javascript-clipboard-api-safari-ios-notallowederror-message#:~:text=the%20example%20code-,javascript,alert(%27success%20html%20copy%27)%7D%2C%0A%20%20%20%20()%20%3D%3E%20%7B%7D%0A%20%20)%3B%0A%7D%2C%200),-Share
        setTimeout(async () => {
            try {
                await navigator.clipboard.writeText(web.text);

                if (!skipToast) {
                    toast(TOAST_MESSAGES.SHARE_CLIPBOARD_SUCCESS, {
                        id: "SHARE_CLIPBOARD_SUCCESS",
                    });
                }
            } catch (error) {
                logger.error("Failed to copy to clipboard", error, {
                    context: "triggerShare",
                    isMobile: false,
                });
            }
        }, 0);
    } else {
        const mobileText = `${mobile.title || ""}\n${mobile.text || ""}\n\n${mobile.url || ""}`.trim();
        const copyToClipboard = async (): Promise<void> => {
            try {
                await navigator.clipboard.writeText(mobileText);
                if (!skipToast) {
                    toast(TOAST_MESSAGES.SHARE_CLIPBOARD_SUCCESS, { id: "SHARE_CLIPBOARD_SUCCESS" });
                }
            } catch (e) {
                logger.error("Failed to copy to clipboard (mobile)", e, {
                    context: "triggerShare",
                    isMobile: true,
                });
                if (!skipToast) toast.error(TOAST_MESSAGES.SHARE_MOBILE_FAIL);
            }
        };

        try {
            if (navigator.share) {
                // Single `text` is more reliable on iOS; some webviews show an empty sheet with title/text/url.
                await navigator.share({ text: mobileText });
            } else {
                await copyToClipboard();
            }
        } catch (err: any) {
            if (err?.name === "AbortError") return;
            logger.error("Mobile share failed", err, {
                context: "triggerShare",
                isMobile: true,
            });
            await copyToClipboard();
        }
    }
};

/**
 * Triggers the share functionality for social content.
 *
 * This function copies the share text to the clipboard for desktop users and uses the native mobile
 * share functionality for mobile users.
 *
 * @param shareData - The share data containing web and mobile properties.
 * @throws Throws an error if there is an issue with the share functionality.
 */
export const triggerShareSocial = async (shareData: SocialShareData): Promise<void> => {
    const { web = { text: "" }, mobile = { text: "", url: "" }, skipToast = false } = shareData;

    // Copy the share text to clipboard for desktop users
    if (!isMobile) {
        await navigator.clipboard.writeText(web.text);

        if (!skipToast) {
            toast(TOAST_MESSAGES.SHARE_CLIPBOARD_SUCCESS, {
                id: "SHARE_CLIPBOARD_SUCCESS",
            });
        }
    } else {
        try {
            // Use native mobile share functionality for mobile users
            if (navigator.share) {
                await navigator.share({
                    text: `${mobile.text || ""}\n\n${mobile.url || ""}`.trim(),
                });
            }
        } catch (err: any) {
            // Only show the error message when its not a share abort error (the user cancelled sharing)
            if (!isNil(err?.name) && err.name !== "AbortError") {
                logger.error("Mobile share failed (social)", err, {
                    context: "triggerShareSocial",
                    isMobile: true,
                });
                toast.error(`${TOAST_MESSAGES.SHARE_MOBILE_FAIL}: ${err}`, {
                    id: "SHARE_MOBILE_FAIL",
                });
            }
        }
    }
};
